import { Debouncer } from '@js-sdk/debouncer';
import { EventEmitter } from '@spotify-internal/emitter';
import { logging } from '@js-sdk/debug-tools';
import { PlaybackEvent, Reason, } from '@js-sdk/playback';
import { GAIA_VOLUME } from '../../typedefs';
import { HarmonyErrors } from '../../enums/errors';
import { TrackPlayerAPIClientError } from '../../error/tp_service/client';
import version from '../../version';
import { defer } from '../helpers/defer';
import { assert } from '../../utils/assert';
const debugLogger = logging.forTag('tpapiclient');
// Used by EndSong tests to be able to snoop EndSongs that were generated by
// the backend.
const endSongLogger = logging.forTag('tpapiclient.endsong');
// Used by testing of new EndSong solution. Will replace old logger when
// solution is rolled out.
const endSongsLogger = logging.forTag('tpapiclient.endsongs');
const DEFAULT_STREAM_INTERVAL = 500;
const DEALER_TRACK_PLAYBACK_FILTER = /^hm:\/\/track-playback\/v1/;
const DEALER_PLAY_LOST_TOKEN_FILTER = /^hm:\/\/play-token\/lost/;
const ENDPOINT = 'http://@webgate/track-playback';
/**
 * The checker function that will be used to determine whether the requests to
 * the Track Playback service should be retried in case of failure.
 *
 * @param response - The HTTP response.
 * @param StatusFamily - the status family enum.
 * @return True if the request should be retried, false otherwise.
 */
const maybeRetry = function (response, StatusFamily) {
    const family = response.getStatusFamily();
    return (family === StatusFamily.SERVER_ERROR ||
        family === StatusFamily.CONNECTION_ERROR);
};
/**
 * A long backoff strategy for request retries.
 *
 */
const LONG_BACKOFF = {
    condition: maybeRetry,
    maxRetries: 8,
};
/**
 * A short backoff strategy for request retries.
 *
 */
const SHORT_BACKOFF = {
    condition: maybeRetry,
    maxRetries: 2,
};
export class TrackPlaybackServiceClient extends EventEmitter {
    constructor(options) {
        super();
        /**
         * The device id of the current device.
         */
        this._deviceId = null;
        /**
         * The  supported file formats for this instance.
         */
        this._manifestFormats = [];
        /**
         * The sequence id that is used to identify individual messages.
         */
        this._sequenceNumber = 0;
        /**
         * The currently playing context.
         */
        this._currentContext = null;
        /**
         * A flag that indicates whether in the process of loading a new track.
         */
        this._loading = false;
        /**
         * The last payload that was sent as a State Update. Used to compare any
         * other payloads.
         */
        this._lastSentStateUpdatePayload = null;
        /**
         * The total amount of time that the client has been streaming, in
         * milliseconds.
         */
        this._totalStreamTime = 0;
        /**
         * A flag that indicates whether we're currently sending a state conflict
         * request.
         */
        this._isSendingConflict = false;
        /**
         * A list containing any rejected states that need to be sent to the state
         * conflict endpoint.
         *
         * Since the client will only have one state conflict request in-flight at any
         * time, any new conflicts will be put into this queue for sending after the
         * in-flight request has finished.
         */
        this._queuedRejectedStates = [];
        /**
         * A flag that indicates whether the client currently has a state update in
         * flight.
         */
        this._isSendingUpdate = false;
        /**
         * A list of string sources for state updates that need to be sent.
         */
        this._queueSendUpdate = [];
        /**
         * A flag indicating whether this instance has successfully registered to
         * the Track Playback service.
         */
        this._registered = false;
        /**
         * A flag that indicates that we are currently in the process of
         * deregistering.
         */
        this._isDeregistering = false;
        /**
         * A deferred that will be resolved once the tracking data for the current
         * track has been finalized.
         *
         */
        this._waitingForTrackingData = defer();
        /**
         * The State ID of the last command processed.
         *
         * We use this value to compare the state_id of the list player events (where
         * it is exposed as the playback id). If they match, it means that we have
         * already sent an optimistic state update, and we ignore any state updates
         * that might be sent.
         */
        this._lastProcessedStateId = null;
        /**
         * When set to true, the client will ensure that only one state update will
         * be sent at a time, and any queued state updates will be coalesced to only
         * send the last most updated state.
         *
         * When set to false, the client will send each state update regardless of the
         * status of the other ones.
         *
         * As of May 16th, this has been turned off for all users, by default.
         */
        this._limitStateUpdates = false;
        /**
         * The timestamp of the last state update. Used to space progress-triggered
         * state updates.
         */
        this._lastUpdateStateTime = 0;
        /**
         * A flag that indicates whether the initial update has been sent.
         */
        this._initialUpdateSent = false;
        /**
         * A flag that indicates that the started playing updates has been sent.
         */
        this._startedPlayingUpdateSent = false;
        // New added below
        this._nextFinalizedDataPayload = null;
        this._switchingContext = false;
        /**
         * The Dealer connection id for the current device.
         */
        this._connectionId = null;
        this._transport = options.transport;
        this._logger = options.logger;
        this._endpoint = options.endpoint || ENDPOINT;
        this._listPlayer = options.listPlayer;
        this._ListClass = options.ListClass;
        this._currentGaiaVolume = options.initialVolume * GAIA_VOLUME;
        this._clientVersion = options.clientVersion;
        this._descriptorPromise = Promise.resolve(options.descriptor);
        this._init();
        // Rebind common functions
        this._performCommand = this._performCommand.bind(this);
        this._handleRegisterResponse = this._handleRegisterResponse.bind(this);
        this._handleStateConflictResponse = this._handleStateConflictResponse.bind(this);
        this.register = this.register.bind(this);
        this._parseDescriptor = this._parseDescriptor.bind(this);
        this._volumeDebouncer = new Debouncer(this._sendVolume.bind(this), {
            blockInitial: true,
        });
    }
    static create(options) {
        return new TrackPlaybackServiceClient(options);
    }
    /**
     * Initializes the client.
     *
     */
    _init() {
        // We start out with a resolved promise in order to ensure that we can
        // deregister successfully if we never played anything.
        this._waitingForTrackingData = defer();
        this._waitingForTrackingData.resolve(true);
        // Attach event listeners
        this._transport.on(this._transport.EVENT_CONNECTION_ID, this._onConnectionId.bind(this));
        this._transport.matchMessages(DEALER_PLAY_LOST_TOKEN_FILTER, this._onPlayTokenLost.bind(this));
        this._transport.matchMessages(DEALER_TRACK_PLAYBACK_FILTER, this._onTrackPlaybackMessage.bind(this));
        this._listPlayer.addListeners({
            [PlaybackEvent.LIST_PLAYER_CAPPED]: this._onCapped.bind(this),
            [PlaybackEvent.LIST_PLAYER_ERROR_SYNC]: this._onError.bind(this),
            [PlaybackEvent.LIST_PLAYER_BEFORE_NEXT]: this._onBeforeTrackChange.bind(this),
            [PlaybackEvent.LIST_PLAYER_BEFORE_PREVIOUS]: this._onBeforeTrackChange.bind(this),
            [PlaybackEvent.LIST_PLAYER_BEFORE_PLAYER_LOAD]: this._onBeforePlayerLoad.bind(this),
            [PlaybackEvent.LIST_PLAYER_PLAYER_LOAD]: this._onListPlayerLoad.bind(this),
            [PlaybackEvent.LIST_PLAYER_BEFORE_TRACK_LOAD]: this._onBeforeTrackLoad.bind(this),
            [PlaybackEvent.LIST_PLAYER_POSITION_CHANGED]: this._onPositionChanged.bind(this),
            [PlaybackEvent.LIST_PLAYER_DURATION_CHANGED]: this._onDurationChanged.bind(this),
            [PlaybackEvent.LIST_PLAYER_VOLUME_CHANGED]: this._onVolumeChanged.bind(this),
            [PlaybackEvent.LIST_PLAYER_PAUSED]: this._onPlayPause.bind(this, true),
            [PlaybackEvent.LIST_PLAYER_PLAYING]: this._onPlayPause.bind(this, false),
            [PlaybackEvent.LIST_PLAYER_PLAYED_THRESHOLD_REACHED]: this._onPlayThresholdReached.bind(this),
            [PlaybackEvent.LIST_PLAYER_PROGRESS]: this._onProgress.bind(this),
            [PlaybackEvent.LIST_PLAYER_TRACKING_DATA_CREATED]: this._onTrackDataCreatedEvent.bind(this),
            [PlaybackEvent.LIST_PLAYER_TRACKING_DATA_FINALIZED]: this._onTrackDataFinalizedEvent.bind(this),
        });
        // Ensure that we react to descriptor change events.
        this._descriptorPromise.then((descriptor) => {
            descriptor.on("descriptor_changed" /* DEVICE_DESCRIPTOR_CHANGED */, () => {
                this._parseDescriptor(descriptor);
                this._updateDeviceInfo();
            });
        });
    }
    /**
     * Handles LIST_PLAYER_BEFORE_NEXT/PREVIOUS events.
     */
    _onBeforeTrackChange() {
        var _a, _b;
        if (this._nextFinalizedDataPayload || this._switchingContext) {
            return;
        }
        const stateRef = (_b = (_a = this._currentContext) === null || _a === void 0 ? void 0 : _a.getStateRef()) !== null && _b !== void 0 ? _b : null;
        const payload = this._generateStatePayload(stateRef, 'track_data_finalized');
        // We set the sequence number here already so we can "reserve" it. This
        // ensures that it will appear as an earlier message to the backend.
        this._setSequenceNumber(payload);
        this._nextFinalizedDataPayload = payload;
    }
    /**
     * Handles LIST_PLAYER_BEFORE_TRACK_LOAD events.
     *
     */
    _onBeforeTrackLoad() {
        this._loading = true;
        this._switchingContext = false;
    }
    /**
     * Handles LIST_PLAYER_BEFORE_PLAYER_LOAD events.
     *
     * @param ev - The event object.
     */
    _onBeforePlayerLoad(ev) {
        const context = this._currentContext;
        if (!context) {
            return;
        }
        const currentTrack = context.getCurrentTrack();
        this._currentTrackDuration = (currentTrack === null || currentTrack === void 0 ? void 0 : currentTrack.metadata.duration) || 0;
        this._currentMediaType = ev.data.track.mediaType.toUpperCase();
        const options = ev.data.options;
        context.setPaused(!options.autoplay);
        this._setCurrentTrackPosition(options ? options.position || 0 : 0);
        // Trigger a state update "optimistically," assuming that the loading of
        // the new track will succeed. We do this to immediately give feedback to
        // observing clients, allowing them to update their UI.
        //
        // Since we didn't actually trigger any playback yet, we'll need to send
        // additional state updates later to correct playback states and to correct
        // and metadata that might not be correct.
        this._updateState('before_track_load');
        this._startedPlayingUpdateSent = false;
        this._initialUpdateSent = false;
    }
    /**
     * Handles LIST_PLAYER_CAPPED events.
     *
     */
    _onCapped() {
        this._listPlayer.clear(Reason.CAPPED);
        this._currentContext = null;
        this._updateState(Reason.CAPPED);
        this._setCurrentTrackPosition(undefined);
        this._lastProcessedStateId = null;
        this.emit("state_cleared" /* TP_API_STATE_CLEARED */, null);
    }
    /**
     * Handles the CONNECTION_ID event from the transport.
     *
     * @param ev - The event.
     */
    _onConnectionId(ev) {
        this._connectionId = ev.data.id;
        if (this._isDeregistering) {
            return;
        }
        this._registered = false;
        Promise.all([
            this._listPlayer.getVolume(),
            this._descriptorPromise.then(this._parseDescriptor),
        ])
            .then(this.register, (error) => {
            debugLogger.info('Skipping registration due to error', error);
            this.emit("registration_aborted" /* TP_API_REGISTRATION_ABORTED */, {
                reason_error: error,
            });
        })
            .catch(() => {
            // Catch any errors related to the actual registration call, but don't
            // act upon it as it will trigger an error anyway.
        });
    }
    /**
     * Handles LIST_PLAYER_DURATION_CHANGED events.
     *
     * @param ev - The event.
     */
    _onDurationChanged(ev) {
        // This method previously triggered a state-update, which was done to ensure
        // that observing clients will be able to sync their displays to the actual
        // duration.
        // However, this led to some unexpected behaviour, so the state update has
        // been removed. We can't guarantee that the duration will be synced between
        // clients until the next update after this--which would most likely be when
        // the "played threshold" has been reached.
        this._currentTrackDuration = ev.data.duration;
        this._emitStateChanged();
    }
    /**
     * Handles LIST_PLAYER_ERROR events.
     *
     * @param ev - The event.
     */
    _onError(ev) {
        if (!('position' in ev.data)) {
            return;
        }
        this._setCurrentTrackPosition(ev.data.position);
        this._updateState('error');
    }
    /**
     * Sets the paused state of the current context.
     *
     * @param paused - The paused state value.
     * @param ev - The paused event.
     */
    _onPlayPause(paused, ev) {
        const context = this._currentContext;
        if (!context) {
            return;
        }
        context.setPaused(paused);
        if ('position' in ev.data) {
            this._setAllTrackPositions(ev.data.position);
        }
        if (ev.data.track.stateId &&
            this._shouldSendUpdateForEvent(ev.data.track.stateId)) {
            this._updateState(paused ? 'pause' : 'resume');
        }
        else {
            this._lastProcessedStateId = null;
            this._emitStateChanged();
        }
    }
    /**
     * Handles a LIST_PLAYER_PLAYED_THRESHOLD_REACHED event.
     *
     * @param ev - The event.
     */
    _onPlayThresholdReached(ev) {
        this._initialUpdateSent = true;
        this._setAllTrackPositions(ev.data.position);
        this._updateState('played_threshold_reached');
    }
    /**
     * Handles a play token lost message from the backend.
     *
     */
    _onPlayTokenLost() {
        this._listPlayer.pause().catch(function () {
            // Catch NO-LIST errors
        });
    }
    /**
     * Handles LIST_PLAYER_POSITION_CHANGED events.
     *
     * @param ev - The event.
     */
    _onPositionChanged(ev) {
        if (this._loading) {
            // We can get this event while another track is loading (from the previously
            // playing track). When this flag is true, we will ignore this event.
            return;
        }
        this._setCurrentTrackPosition(ev.data.position);
        if (ev.data.track &&
            this._shouldSendUpdateForEvent(ev.data.track.stateId)) {
            this._updateState('position_changed');
        }
        else {
            this._lastProcessedStateId = null;
            this._emitStateChanged();
        }
    }
    /**
     * Handles LIST_PLAYER_PROGRESS events.
     *
     * @param ev - The event.
     */
    _onProgress(ev) {
        var _a;
        if (this._loading || !this._currentContext) {
            return;
        }
        this._setAllTrackPositions(ev.data.position);
        if (!this._startedPlayingUpdateSent && ev.data.played > 1000) {
            this._startedPlayingUpdateSent = true;
            this._updateState('started_playing');
        }
        const logData = ev.data.logData;
        if (!logData) {
            return;
        }
        // Track the stream time
        if (!logData.noLog) {
            this._totalStreamTime += (_a = ev.data.interval) !== null && _a !== void 0 ? _a : DEFAULT_STREAM_INTERVAL;
        }
        const currentTrack = this._currentContext.getCurrentTrack();
        const playingUpdateInterval = currentTrack &&
            typeof currentTrack.ms_playing_update_interval === 'number'
            ? currentTrack.ms_playing_update_interval
            : null;
        if (this._initialUpdateSent &&
            playingUpdateInterval !== null &&
            playingUpdateInterval > 0 &&
            Date.now() - this._lastUpdateStateTime > playingUpdateInterval) {
            this._updateState('progress');
        }
    }
    /**
     * Handles LIST_PLAYER_TRACKING_DATA_CREATED events.
     */
    _onTrackDataCreatedEvent() {
        this._waitingForTrackingData = defer();
    }
    /**
     * Handles LIST_PLAYER_TRACKING_DATA_FINALIZED events.
     *
     * @param ev - The event.
     */
    _onTrackDataFinalizedEvent(ev) {
        const payload = this._nextFinalizedDataPayload;
        if (payload) {
            payload.playback_stats = ev.data.playbackStats;
            this._updateState('track_data_finalized', payload);
        }
        this._nextFinalizedDataPayload = null;
        if (this._waitingForTrackingData) {
            this._waitingForTrackingData.resolve(true);
        }
    }
    /**
     * Handles LIST_PLAYER_PLAYER_LOAD events.
     * Note: The previous comment here said that this handler handled LIST_PLAYER_TRACK_LOADED events
     * but code said otherwise.
     *
     * @param ev - The event.
     */
    _onListPlayerLoad(ev) {
        this._loading = false;
        const context = this._currentContext;
        if (!context) {
            return;
        }
        context.setPaused(!ev.data.autoplay);
        this._setCurrentTrackPosition(ev.data.position || 0);
    }
    /**
     * Handles a message from the Track Playback service.
     *
     * @param msg - The dealer message.
     */
    _onTrackPlaybackMessage(msg) {
        const payloads = msg.payloads;
        if (!Array.isArray(payloads) || !payloads.length) {
            return;
        }
        this._performCommand(payloads[0]);
    }
    /**
     * Handles LIST_PLAYER_VOLUME_CHANGED events.
     *
     * @param ev - The event.
     */
    _onVolumeChanged(ev) {
        var _a;
        this._currentGaiaVolume = ev.data.volume * GAIA_VOLUME;
        if (!this._registered) {
            // The client is not registered, so we should not send a volume change
            // event for now.
            return;
        }
        const commandId = (_a = ev.data.commandId) !== null && _a !== void 0 ? _a : '';
        const payload = {
            seq_num: undefined,
            volume: this._currentGaiaVolume,
            command_id: commandId,
        };
        // Use the volume debouncer instead of calling the method directly.
        this._volumeDebouncer.async(payload);
    }
    /**
     * Clears the session-specific data.
     *
     */
    _clearSessionData() {
        this._connectionId = null;
        this._sequenceNumber = 0;
        this._currentContext = null;
        this._lastSentStateUpdatePayload = null;
        this._totalStreamTime = 0;
        this._isSendingConflict = false;
        this._isSendingUpdate = false;
        this._queueSendUpdate = [];
        this._previousTrackPosition = undefined;
        this._currentTrackPosition = undefined;
        this._currentTrackDuration = undefined;
        this._currentMediaType = undefined;
        this._lastProcessedStateId = null;
        this.emit("state_cleared" /* TP_API_STATE_CLEARED */, null);
    }
    /**
     * Creates a state reference object from an internal state reference.
     *
     * @param stateMachine - The state machine that will be used to create the reference.
     * @param internalStateRef - The internal state reference.
     * @return The created state reference.
     */
    _createStateRef(stateMachine, internalStateRef) {
        if (!internalStateRef) {
            // A null internal state reference should always return a null state
            // reference.
            return null;
        }
        const state = stateMachine.states[internalStateRef.state_index];
        if (!state) {
            throw new TrackPlayerAPIClientError(HarmonyErrors.TP_CANNOT_CREATE_STATE_REF, 'Invalid state reference.');
        }
        return {
            state_machine_id: stateMachine.state_machine_id,
            state_id: state.state_id,
            paused: internalStateRef.paused,
        };
    }
    /**
     * Sends a deregistration request to the Track Playback Service.
     *
     * @param lastPlayerState - The ListPlayer state prior to deregistration.
     * @return True if the deregistration request was successfully sent, false otherwise.
     */
    _deregisterFromService(lastPlayerState) {
        let stateRef = null;
        const currentContext = this._currentContext;
        if (currentContext) {
            if (lastPlayerState) {
                currentContext.setPaused(!lastPlayerState.playing);
                this._setAllTrackPositions(lastPlayerState.position);
            }
            stateRef = currentContext.getStateRef();
        }
        const payload = this._generateStatePayload(stateRef, 'deregister');
        this._setSequenceNumber(payload);
        const endpoint = `${this._endpoint}/v1/devices/${this._deviceId}`;
        return this._transport
            .request(endpoint, {
            authorize: true,
            method: 'DELETE',
            payload: JSON.stringify(payload),
            responseType: 'json',
            retry: SHORT_BACKOFF,
        })
            .then((response) => {
            if (response.status !== 200 && response.status !== 204) {
                // Since we "technically failed", we should reset the value of this flag
                // so we can retry.
                this._registered = true;
                return false;
            }
            // Check the response body and log any EndSongs that might have been sent
            // back.
            const { body } = response;
            if (body) {
                if (body.endsong) {
                    endSongLogger.log(body.endsong);
                }
                if (body.endsongs) {
                    endSongsLogger.log(body.endsongs);
                }
            }
            // Successfully deregistered.
            this._clearSessionData();
            this.emit("deregistered" /* TP_API_DEREGISTERED */, null);
            return true;
        });
    }
    /**
     * Emits an error event.
     *
     * @param error - The error object.
     * @param jsonData - Additional data for the error as JSON.
     */
    _emitError(error, jsonData = {}) {
        // Log the error
        this._logger
            .logJSSDKError({
            source: 'tpapi-client',
            source_version: version.tagged,
            type: error.code,
            message: error && error.message,
            stack: error && error.stack,
            json_data: jsonData,
            json_data_version: '1.0.0',
        }, 'object')
            .catch(function (e) {
            debugLogger.error('Track-Playback Logging Error', e);
        });
        // Emit the error
        this.emit("error" /* TP_API_ERROR */, {
            error: error,
            data: jsonData,
        });
    }
    /**
     * Emits a `state_changed` event.
     *
     */
    _emitStateChanged() {
        const context = this._currentContext;
        if (!context) {
            return;
        }
        this.emit("state_changed" /* TP_API_STATE_CHANGED */, {
            stateMachine: context.getStateMachine(),
            stateRef: context.getInternalStateRef(),
            position: this._currentTrackPosition || 0,
            duration: this._currentTrackDuration || 0,
            paused: context.isPaused(),
        });
    }
    /**
     * Creates a state payload that can be sent for any backend requests that need
     * the current device state.
     *
     * @param stateRef - The StateRef object for the payload.
     * @param src - An optional reason for the payload generation.
     * @return The generated payload.
     */
    _generateStatePayload(stateRef, src) {
        // Note: values are set to "undefined" by default in some cases so the
        // corresponding keys are removed during JSON.stringify serialization.
        return {
            seq_num: undefined,
            seq_nums: undefined,
            state_ref: stateRef,
            sub_state: {
                playback_speed: stateRef && !stateRef.paused ? 1 : 0,
                position: this._currentTrackPosition,
                duration: this._currentTrackDuration || undefined,
                stream_time: this._totalStreamTime,
                media_type: this._currentMediaType,
            },
            previous_position: this._previousTrackPosition,
            // Leave this undefined as it will be filled by the TRACK_DATA_FINALIZED
            // event.
            playback_stats: undefined,
            // Placeholder for state conflict payloads.
            rejected_state_refs: undefined,
            // This is an unused field that we set in order to debug where the state
            // update is coming from.
            debug_source: src,
        };
    }
    /**
     * Parses the result of a registration request to the Track Playback service.
     *
     * @param response - The response to parse.
     * @return The body of the response.
     */
    _handleRegisterResponse(response) {
        let error;
        if (response.status === 429) {
            // This case happens when a particular client creates too many connections
            // for a single user.
            error = TrackPlayerAPIClientError.fatal(HarmonyErrors.TP_MAX_SUBSCRIPTIONS_REACHED, 'Max connections reached');
            error.maxedSubscriptions = true;
            error.status = response.status;
            this.emit("max_subscriptions_reached" /* TP_API_MAX_SUBSCRIPTIONS_REACHED */, {
                error,
            });
            return Promise.reject(error);
        }
        if (response.status === 403 &&
            response.body &&
            response.body.error_type === 'PREMIUM_REQUIRED') {
            // For non-whitelisted clients, registering to the Track Playback service
            // with a non-premium user will result in an error.
            error = TrackPlayerAPIClientError.fatal(HarmonyErrors.TP_REGISTRATION_FAILED_NON_PREMIUM, 'Cannot register to Track Playback with non-premium user.');
        }
        else if (response.status !== 200) {
            // We expect that a successful response will always have 200. If not, then
            // it's probably an error.
            error = TrackPlayerAPIClientError.fatal(HarmonyErrors.TP_REGISTRATION_FAILED_WITH_STATUS, `track-playback service responded with status ${response.status} when registering device`);
        }
        else if (!response.body) {
            // We always expect a response body if the registration was successful.
            error = TrackPlayerAPIClientError.fatal(HarmonyErrors.TP_NO_RESPONSE_BODY, 'Unexpected empty response body from registration request.');
        }
        if (error) {
            error.status = response.status;
            return Promise.reject(error);
        }
        // All initial checks passed, set local flags from the response.
        const initialState = response.body;
        // Logging values
        if (initialState.endsong) {
            endSongLogger.log(initialState.endsong);
        }
        if (initialState.endsongs) {
            endSongsLogger.log(initialState.endsongs);
        }
        this._sequenceNumber = initialState.initial_seq_num;
        this._registered = true;
        this.emit("registered" /* TP_API_REGISTERED */, { deviceId: this._deviceId });
        return Promise.resolve(true);
    }
    /**
     * Parses the response from the state conflict request, processesing any
     * commands that might have been included.
     *
     * @param response - The HTTP response to parse.
     */
    _handleStateConflictResponse(response) {
        var _a;
        if (response.status >= 200 && response.status < 300) {
            // Successful response
            const commands = (_a = response.body) === null || _a === void 0 ? void 0 : _a.commands;
            if (!commands || !commands.length) {
                return;
            }
            for (let i = 0, l = commands.length; i < l; i++) {
                this._performCommand(commands[i]);
            }
        }
        else {
            const error = new TrackPlayerAPIClientError(HarmonyErrors.TP_CONFLICT_REQUEST_FAILED_WITH_STATUS, `Track-Playback service responded with ${response.status}`);
            error.status = response.status;
            this._emitError(error);
        }
    }
    /**
     * Handles the response to a state update request.
     *
     * @param sentStateRef - The state reference that was sent for the
     *     request. Used for comparison after the response has been received to see
     *     whether the new state is still valid.
     * @param response - The HTTP response for the state update request.
     * @return The body of the response.
     */
    _handleStateUpdateResponse(sentStateRef, response) {
        let error;
        if (response.status !== 200) {
            error = new TrackPlayerAPIClientError(HarmonyErrors.TP_PARSE_STATE_UPDATE_FAILED_WITH_STATUS, `Service responded with status ${response.status}`);
            error.status = response.status;
            return Promise.reject(error);
        }
        if (!response.body) {
            error = new TrackPlayerAPIClientError(HarmonyErrors.TP_UPDATE_REQUEST_EMPTY_RESPONSE, 'Unexpected empty response body from state update request.');
            error.status = response.status;
            return Promise.reject(error);
        }
        //  Success, continue with parsing the response.
        const command = response.body;
        // Log any endsongs from the response.
        if (command.endsong) {
            endSongLogger.log(command.endsong);
        }
        if (command.endsongs) {
            endSongsLogger.log(command.endsongs);
        }
        const context = this._currentContext;
        if (!context) {
            // Nothing is playing, so there's no state to update.
            return Promise.resolve();
        }
        const newStateMachine = command.state_machine;
        const updatedInternalStateRef = command.updated_state_ref;
        if (!this._isCurrentStateRef(sentStateRef)) {
            // The updated state is no longer valid. We therefore ignore the new state
            // from the response.
            return Promise.resolve();
        }
        // Replace our current state with the updated state from the backend.
        context.setStateMachine(newStateMachine);
        context.setCurrentState(updatedInternalStateRef);
        this._emitStateChanged();
        return Promise.resolve();
    }
    /**
     * Checks whether the provided state reference is the same as the currently
     * known state reference.
     *
     * @param stateRef - The state reference to compare with the current state reference.
     * @return True if the provided state reference is the same as the
     *     current state reference, false otherwise.
     */
    _isCurrentStateRef(stateRef) {
        const currentStateRef = this._currentContext
            ? this._currentContext.getStateRef()
            : null;
        if (!currentStateRef && !stateRef) {
            return true;
        }
        else if (!currentStateRef || !stateRef) {
            return false;
        }
        return (currentStateRef.state_machine_id === stateRef.state_machine_id &&
            currentStateRef.state_id === stateRef.state_id &&
            currentStateRef.paused === stateRef.paused);
    }
    /**
     * Logs an unsent state update request to the backend.
     *
     * @param payload - The payload that was not sent.
     */
    _logUnsentStateUpdate(payload) {
        debugLogger.info('Unsent state update.', payload);
        this._logger
            .logClientEvent({
            source: 'tpapi-client',
            source_version: version.tagged,
            source_vendor: 'spotify',
            event: 'unsent-state-update',
            event_version: '1.0.0',
            json_data: payload,
        }, 'object')
            .catch((e) => {
            debugLogger.error('Track-Playback Logging Error', e);
        });
    }
    /**
     * Stops playback and deregisters the client.
     *
     */
    _logout() {
        this._listPlayer.pause().catch(() => {
            // Sometimes we will have no list, but we still want to pause, so we
            // so we add a catch block to prevent the error from bubbling into the
            // console.
        });
        this.emit("state_cleared" /* TP_API_STATE_CLEARED */, null);
        this.deregister().then((success) => {
            this._currentContext = null;
            this._setCurrentTrackPosition(undefined);
            if (success) {
                this.emit("logout" /* TP_API_LOGOUT */, null);
            }
        });
    }
    /**
     * Parses a device descriptor and sets internal flags.
     *
     * @param descriptor - The device descriptor to parse.
     * @return Returns true.
     */
    _parseDescriptor(descriptor) {
        this._deviceId = descriptor.getId();
        this._deviceInfo = descriptor.toTrackPlaybackDeviceInfo();
        this._manifestFormats = descriptor.getManifestFormats();
        return true;
    }
    /*
     * Parses and runs a command from the Track Playback service.
     *
     * @param command The command to perform.
     */
    _performCommand(command) {
        switch (command.type) {
            case 'set_volume':
                this._setVolume(command);
                break;
            case 'log_out':
                this._logout();
                break;
            case 'replace_state':
                this._replaceState(command);
                break;
            case 'ping':
                this._updateState('ping');
                break;
            default:
                this._emitError(new TrackPlayerAPIClientError(HarmonyErrors.TP_UNKNOWN_COMMAND, 'Received unknown command.'), { command: command });
        }
    }
    /**
     * Rejects a state by sending a state conflict request.
     *
     * @param rejectedState - The state that was rejected. If not-provided it will trigger
     * sending any queued rejected states.
     */
    _rejectState(rejectedState) {
        const queuedRejectedStates = this._queuedRejectedStates;
        // We add the state to the queue already, even though we might send it
        // immediately. This simplifies the code to always send the queue, rather
        // than to recreate the queue every time.
        if (rejectedState !== undefined) {
            queuedRejectedStates.push(rejectedState);
        }
        if (!this._registered ||
            this._isSendingConflict ||
            !queuedRejectedStates.length) {
            // If we are not registered, or if we have a state conflict in-flight or
            // if there's nothing to send, return early.
            return;
        }
        this._isSendingConflict = true;
        // Get all conflicts to be sent from the queue, removing them from the array
        // (thereby emptying the queue again).
        const rejectedStates = queuedRejectedStates.splice(0, 5);
        const stateRef = this._currentContext
            ? this._currentContext.getStateRef()
            : null;
        const payload = this._generateStatePayload(stateRef);
        payload.rejected_state_refs = rejectedStates;
        this._setSequenceNumbers(payload, rejectedStates.length);
        // Called after the request to reset the state conflict sending flags.
        const resetConflictFlags = () => {
            this._isSendingConflict = false;
            this._rejectState();
        };
        const endpoint = `${this._endpoint}/v1/devices/${this._deviceId}/state_conflict`;
        this._transport
            .request(endpoint, {
            authorize: true,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'json',
            payload: JSON.stringify(payload),
            retry: SHORT_BACKOFF,
        })
            .then(this._handleStateConflictResponse)
            .then(resetConflictFlags, resetConflictFlags);
    }
    /**
     * Replaces the currently known state with a state given by a command.
     *
     * @param command - The ReplaceState command that contains the new state.
     */
    _replaceState(command) {
        var _a, _b, _c;
        const listPlayer = this._listPlayer;
        const cmdStateMachine = command.state_machine;
        const cmdInternalStateRef = command.state_ref;
        const newStateRef = this._createStateRef(cmdStateMachine, cmdInternalStateRef);
        if (!this._isCurrentStateRef(command.prev_state_ref)) {
            // The backend thinks that the client is in a different state. This can
            // happen the client's state changes before it's successfully notified the
            // backend about the new state.
            //
            // This case is called a "state conflict" and the client needs to send this
            // conflicting state to the backend so that it can reapply any operations
            // to the new state.
            this._rejectState(newStateRef);
            return;
        }
        if (!cmdInternalStateRef) {
            // When we get a null state, it means that we should clear the context.
            this._clearContextAndState();
            return;
        }
        const currentStateRef = (_b = (_a = this._currentContext) === null || _a === void 0 ? void 0 : _a.getStateRef()) !== null && _b !== void 0 ? _b : null;
        // There's no way right now that `newStateRef` would be null at this point.
        assert(newStateRef !== null, 'New state reference is null');
        if (this._currentContext &&
            (currentStateRef === null || currentStateRef === void 0 ? void 0 : currentStateRef.state_id) === newStateRef.state_id) {
            // Same state ids means that this is a track operation, so we don't need to
            // create a new context.
            const currentContext = this._currentContext;
            currentContext.setStateMachine(cmdStateMachine);
            currentContext.setCurrentState(cmdInternalStateRef);
            // Flag to determine whether there's an operation from this state machine
            // that will trigger a state update.
            let willUpdate = false;
            // If the current paused state is not the same as the old paused state, it
            // will most likely trigger an event, so we set this here.
            if (currentContext.isPaused() !== cmdInternalStateRef.paused) {
                willUpdate = true;
            }
            if (cmdInternalStateRef.paused) {
                currentContext.setPaused(true);
                listPlayer.pause();
            }
            else {
                currentContext.setPaused(false);
                listPlayer.resume();
            }
            const seekPosition = parseInt(command.seek_to, 10);
            if (currentContext.allowSeeking() && !isNaN(seekPosition)) {
                listPlayer.seek(seekPosition);
                // Assume that we'll have a successful seek.
                this._setCurrentTrackPosition(seekPosition);
                willUpdate = true;
            }
            if (willUpdate) {
                // There will be a separate state update from one of the above operations,
                // so just wait for that instead of doing a separate one right now.
                // However, we should emit a state change since that's not automatically
                // handled for us anymore.
                this._emitStateChanged();
            }
            else {
                // Set this to null to allow for the next updates to all propagate.
                this._lastProcessedStateId = null;
                this._updateState('modify_current_state');
            }
        }
        else {
            this._lastProcessedStateId = (_c = newStateRef.state_id) !== null && _c !== void 0 ? _c : null;
            // Replacing the context will stop the current track
            if (this._currentContext) {
                this._onBeforeTrackChange();
            }
            // The id of the command's state is not the same as the current state id
            // that we have. This means that we will have to create a new context and
            // load it for playback.
            const newContext = this._ListClass.create('spotify:app:jsspeaker', this._manifestFormats);
            newContext.setStateMachine(cmdStateMachine);
            newContext.startAtState(cmdInternalStateRef);
            if (this._deviceId) {
                newContext.setDeviceId(this._deviceId);
            }
            this._currentContext = newContext;
            // We need to set the initial position of the first track to be played in
            // the context. While individual states may contain their starting position,
            // this field from the command overrides any position that might be defined
            // in the state.
            const seekTo = command.seek_to || 0;
            newContext.setInitialPosition(seekTo);
            // For reporting the track duration, we start out with the duration that's
            // provided by the metadata in the State Machine. This might or might not be
            // equal to the actual value of the track duration (differences between
            // different formats or encodings), so this is expected to be corrected
            // later when we get the duration_changed event.
            // const track = newContext.getCurrentTrack();
            // this._currentTrackDuration = track ? track.metadata.duration : 0;
            this._loading = true;
            this._switchingContext = true;
            listPlayer.play(newContext, { reason: Reason.REMOTE });
        }
    }
    _clearContextAndState() {
        const listPlayer = this._listPlayer;
        listPlayer.pause().catch(function () {
            // Sometimes we will have no list, but we still want to pause, so we
            // so we add a catch block to prevent the error from bubbling into the
            // console.
        });
        listPlayer.clear(Reason.REMOTE);
        this._currentContext = null;
        this._updateState('state_clear');
        this._setAllTrackPositions(undefined);
        this._lastProcessedStateId = null;
        this.emit("state_cleared" /* TP_API_STATE_CLEARED */, null);
        // We set this as true to suppress any position-related events that will
        // happen in response to clearing the track.
        this._loading = true;
        return;
    }
    /**
     * Sends a devices request to the Track Playback API.
     *
     * @param method - The HTTP verb to use for the request.
     * @param payload -The payload to send.
     * @return The response for the request.
     */
    _sendDevicesRequest(method, payload) {
        let endpoint = `${this._endpoint}/v1/devices`;
        if (method === 'PUT') {
            endpoint = `${endpoint}/${this._deviceId}`;
        }
        return this._transport.request(endpoint, {
            authorize: true,
            method: method || 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'json',
            payload: JSON.stringify(payload),
            retry: LONG_BACKOFF,
        });
    }
    /**
     * Sends a volume request to the Track Playback service to denote a change in
     * the local volume.
     *
     * @param payload -The JSON payload for the request.
     * @return The response.
     */
    _sendVolume(payload) {
        const endpoint = `${this._endpoint}/v1/devices/${this._deviceId}/volume`;
        this._setSequenceNumber(payload);
        return this._transport.request(endpoint, {
            authorize: true,
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            payload: JSON.stringify(payload),
        });
    }
    /**
     * Sets the current and the previous position of the track to the provided
     * value.
     *
     * @param position - The position in milliseconds.
     */
    _setAllTrackPositions(position) {
        this._previousTrackPosition = position;
        this._currentTrackPosition = position;
    }
    /**
     * Set the current position of the track.
     *
     * @param position - The position in milliseconds.
     */
    _setCurrentTrackPosition(position) {
        this._previousTrackPosition = this._currentTrackPosition;
        this._currentTrackPosition = position;
    }
    /**
     * Sets the sequence number of a request payload.
     *
     * @param payload - The payload.
     * @return The payload with the `seq_num` property set.
     */
    _setSequenceNumber(payload) {
        if (payload.seq_num === undefined) {
            payload.seq_num = ++this._sequenceNumber;
        }
        return payload;
    }
    /**
     * Sets the seq_nums property of an object payload to a set of sequence numbers.
     *
     * @param payload - The payload.
     * @param count - The number of sequence numbers to add.
     * @return The payload with the `seq_nums` property set to an array
     * containing a number of sequence numbers equal to `count`.
     */
    _setSequenceNumbers(payload, count) {
        payload.seq_nums = [];
        for (let i = 0; i < count; i++) {
            payload.seq_nums.push(++this._sequenceNumber);
        }
        return payload;
    }
    /**
     * Parses a set_volume command.
     *
     * @param command - The command to process.
     */
    _setVolume(command) {
        const volume = command.volume / GAIA_VOLUME;
        this._listPlayer.setVolume(volume, command.command_id);
    }
    /**
     * Checks whether we should send a state update for a particular state id.
     *
     * @param stateId - The state id to check.
     * @return True if we should send a state update, false otherwise.
     */
    _shouldSendUpdateForEvent(stateId) {
        return !!(stateId && stateId !== this._lastProcessedStateId);
    }
    /**
     * Sends an updated device info to the backend.
     *
     */
    _updateDeviceInfo() {
        if (!this._registered || !this._deviceInfo) {
            return;
        }
        this._sendDevicesRequest('PUT', this._deviceInfo).then((response) => {
            if (response.status === 204) {
                this.emit("registration_updated" /* TP_API_REGISTRATION_UPDATED */, null);
            }
        });
    }
    /**
     * Sends an update of the current state to the backend.
     *
     * @param source - A string that indicates the reason for the update.
     * @param overridePayload - An object that will be used as a payload
     *     instead of a generated one.
     */
    _updateState(source, overridePayload) {
        var _a, _b;
        if (!this._registered) {
            return;
        }
        let stateRef;
        let payload;
        if (overridePayload) {
            stateRef = overridePayload.state_ref;
            payload = overridePayload;
        }
        else {
            stateRef = (_b = (_a = this._currentContext) === null || _a === void 0 ? void 0 : _a.getStateRef()) !== null && _b !== void 0 ? _b : null;
            payload = this._generateStatePayload(stateRef, source);
        }
        if (!this._wasStatePayloadSentRecently(payload)) {
            // We've already sent this payload recently, so we don't resend a duplicate.
            debugLogger.info('State update ignored (duplicate).', payload);
            return;
        }
        // Emit a state change prior to sending so that local observers can update the
        // UI.
        this._emitStateChanged();
        if (this._isSendingUpdate && this._limitStateUpdates) {
            // If _limitStateUpdates is true, we want to ensure that we only have one
            // state update in flight at any given time. We would therefore queue any
            // state update operations for later.
            this._logUnsentStateUpdate(payload);
            this._queueSendUpdate.push(source);
            return;
        }
        this._isSendingUpdate = true;
        this._queueSendUpdate = [];
        this._setSequenceNumber(payload);
        this._lastSentStateUpdatePayload = payload;
        // Called after the request to reset the flags.
        const resetStateUpdateFlags = () => {
            this._isSendingUpdate = false;
            if (this._queueSendUpdate.length) {
                this._updateState(this._queueSendUpdate.join(','));
            }
            debugLogger.info('State update sent.', source, payload);
        };
        const endpoint = `${this._endpoint}/v1/devices/${this._deviceId}/state`;
        this._lastUpdateStateTime = Date.now();
        this._transport
            .request(endpoint, {
            authorize: true,
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'json',
            payload: JSON.stringify(payload),
            retry: LONG_BACKOFF,
        })
            .then(this._handleStateUpdateResponse.bind(this, stateRef))
            .then(resetStateUpdateFlags, (error) => {
            this._emitError(error);
            resetStateUpdateFlags();
        });
    }
    /**
     * Compares a state update payload to the last sent payload to see if they are
     * the same.
     *
     * @param payload - The payload to compare.
     * @return True if the payload is the same as the last sent payload,
     *     false otherwise.
     */
    _wasStatePayloadSentRecently(payload) {
        const lastPayload = this._lastSentStateUpdatePayload;
        if (!lastPayload || !lastPayload.state_ref || !payload.state_ref) {
            // Null state
            return true;
        }
        // Compare state-ref
        const pStateRef = payload.state_ref;
        const lStateRef = lastPayload.state_ref;
        if (pStateRef.paused !== lStateRef.paused ||
            pStateRef.state_id !== lStateRef.state_id ||
            pStateRef.state_machine_id !== lStateRef.state_machine_id) {
            return true;
        }
        // Compare sub state
        const pSubState = payload.sub_state;
        const lSubState = lastPayload.sub_state;
        if (pSubState.playback_speed !== lSubState.playback_speed ||
            pSubState.position !== lSubState.position ||
            pSubState.duration !== lSubState.duration) {
            return true;
        }
        // Compare previous positions
        if (payload.previous_position !== lastPayload.previous_position) {
            return true;
        }
        return false;
    }
    /**
     * Stop playback and clear local state. The device reamins registered with
     * Track Playback but loses active device status.
     *
     * @return true if successful and false otherwise
     */
    stop() {
        if (!this._registered || !this._currentContext) {
            return false;
        }
        this._clearContextAndState();
        return true;
    }
    /**
     * Deregisters the client from the Track Playback service.
     *
     * @return True if the device was successfully deregistered, false otherwise.
     */
    deregister() {
        if (!this._registered) {
            return Promise.resolve(false);
        }
        this._isDeregistering = true;
        // We optimistically set ourselves as deregistered to prevent any other
        // request from being sent.
        this._registered = false;
        // we should update some internal variables before we send the deregister
        // request.
        return this._listPlayer.getPlayerState().then((playerState) => {
            return Promise.all([
                this._deregisterFromService(playerState),
                this._waitingForTrackingData.promise,
                this._listPlayer.stop().catch(() => {
                    /* noop */
                }),
            ]).then(([response]) => {
                this._isDeregistering = false;
                return response;
            });
        }, () => {
            this._isDeregistering = false;
            // Most likely a NO_TRACK_PLAYER error, which we can ignore.
            this._registered = true;
            return false;
        });
    }
    /**
     * Registers the client as a new device to the Track Playback service.
     *
     * @return True if the device was successfully registered,
     *     false otherwise.
     */
    register() {
        if (this._registered) {
            return Promise.resolve(false);
        }
        if (!this._deviceInfo) {
            return Promise.reject(new Error('Need DeviceInfo for device registration.'));
        }
        if (!this._connectionId) {
            return Promise.reject(new Error('Need connection-id for device registration'));
        }
        let prevState;
        const context = this._currentContext;
        if (context) {
            // If there's a context, we need to set the `previous_session_state` value
            // to the current state. This is the case when we're reregistering after a
            // temporary disconnection.
            prevState = this._generateStatePayload(context.getStateRef(), 'register');
            this._setSequenceNumber(prevState);
        }
        return this._sendDevicesRequest('POST', {
            device: this._deviceInfo,
            connection_id: this._connectionId,
            client_version: this._clientVersion,
            previous_session_state: prevState,
            volume: this._currentGaiaVolume,
        })
            .then(this._handleRegisterResponse)
            .catch((e) => {
            if (e && !e.maxedSubscriptions) {
                e.registration = true;
                this._emitError(e);
            }
            return false;
        });
    }
}
//# sourceMappingURL=client.js.map